import { Fade, Tab, Tabs } from '@mui/material';
import type { UsageProductInput } from '@weft/types';
import { DEFAULT_CURRENCY } from '@weft/types';
import { isNotNullOrUndefined, validateEnum, withDefault } from '@weft/utils';
import { withSuspenseBoundary } from 'apps/app/src/components/utility/SuspenseBoundary';
import { useNetSuiteProducts } from 'apps/app/src/hooks/requests/use-net-suite';
import { get } from 'lodash';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { useCreatePlanContext } from '../../../../../contexts/create-plan-context';
import { useFormErrorNotificationContext } from '../../../../../contexts/form-error-notification-context/form-error-notification-context';
import type { PlanInputForm } from '../CreatePlanForm/create-plan-form-types/plan-input-form';
import { TabPanel } from './create-plan-page-tabs-components/TabPanel';
import type { CreatePlanTab } from './create-plan-page-types/create-plan-tab';
import { CREATE_PLAN_TABS } from './create-plan-page-types/create-plan-tab';
import { getCreatePlanTabs } from './create-plan-tabs-consts/create-plan-tabs';

const CreatePlanPageTabs: React.FC = () => {
  const { formState: { errors }, watch } = useFormContext<PlanInputForm>();
  const { createPlanTab: currentTab, setCreatePlanTab: setCurrentTab } = useCreatePlanContext();
  const { errorsFields } = useFormErrorNotificationContext<PlanInputForm, CreatePlanTab>();
  useNetSuiteProducts({ suspense: true });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    validateEnum(newValue, CREATE_PLAN_TABS);
    setCurrentTab(newValue);
  };

  const createPlanTabs = getCreatePlanTabs({
    fixedProducts: [...withDefault(watch('fixedProducts'), [])],
    usageProducts: [...withDefault(watch('usageProducts') as UsageProductInput[], [])],
    promotions: [...withDefault(watch('promotions'), [])],
    currency: DEFAULT_CURRENCY,
    doesHaveError: (tab) => errorsFields.some(errorField => errorField.tab === tab && isNotNullOrUndefined(get(errors, errorField.fieldPath))),
  });

  return (
    <>
      <Tabs
        value={currentTab}
        onChange={handleTabChange}
      >
        {Object.entries(createPlanTabs).map(([createPlanTab, { id, label, selectorAttribute }]) => (
          <Tab
            key={id}
            label={label}
            value={createPlanTab}
            className={twMerge('normal-case group font-normal', createPlanTab === currentTab ? 'is-selected' : 'text-[#94A0A4]')}
            {...selectorAttribute}
          />
        ))}
      </Tabs>

      {Object.entries(createPlanTabs).map(([createPlanTab, { id, render }]) => (
        <TabPanel
          key={id}
          hidden={currentTab !== createPlanTab}
        >
          <Fade in={currentTab === createPlanTab}>
            <div className="h-full w-full">
              {render}
            </div>
          </Fade>
        </TabPanel>
      ))}
    </>
  );
};

const CreatePlanPageTabsWithSuspense = withSuspenseBoundary(CreatePlanPageTabs);
export { CreatePlanPageTabsWithSuspense as CreatePlanPageTabs };